import React, { useState } from 'react';
import Stack from '@mui/joy/Stack';
import Textarea from '@mui/joy/Textarea';
import Button from '@mui/joy/Button';
import Checkbox from '@mui/joy/Checkbox';
import Typography from '@mui/joy/Typography';
import { PaperPlaneTilt as SendRounded, Lock as LockOutlined } from '@phosphor-icons/react';
import { useCreateMessage } from '../../hooks/useSupport';

interface Props {
  ticketId: string;
  isSuperUser?: boolean;
  onSent?: () => void;
}

export default function TicketReplyComposer({ ticketId, isSuperUser, onSent }: Props) {
  const [body, setBody] = useState('');
  const [isInternalNote, setIsInternalNote] = useState(false);
  const createMessage = useCreateMessage(ticketId);

  const handleSend = async () => {
    const text = body.trim();
    if (!text || createMessage.isPending) return;
    try {
      await createMessage.mutateAsync({ body: text, is_internal_note: isInternalNote });
      setBody('');
      setIsInternalNote(false);
      onSent?.();
    } catch {
      // error toast handled by parent
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Stack spacing={1.5}>
      {isInternalNote && (
        <Typography level="body-xs" sx={{ color: 'warning.600', fontWeight: 500 }}>
          Internal note — not visible to the customer
        </Typography>
      )}
      <Textarea
        placeholder={isInternalNote ? 'Write an internal note…' : 'Write a reply…'}
        value={body}
        onChange={e => setBody(e.target.value)}
        onKeyDown={handleKeyDown}
        minRows={2}
        maxRows={5}
        sx={{ fontSize: 'sm', bgcolor: isInternalNote ? 'warning.softBg' : undefined }}
      />
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        {isSuperUser ? (
          <Checkbox
            size="sm"
            label={
              <Stack direction="row" spacing={0.5} alignItems="center">
                <LockOutlined size={14} />
                <span>Internal note</span>
              </Stack>
            }
            checked={isInternalNote}
            onChange={e => setIsInternalNote(e.target.checked)}
          />
        ) : (
          <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
            ⌘ + Enter to send
          </Typography>
        )}
        <Button
          variant="solid"
          color="primary"
          size="sm"
          startDecorator={<SendRounded />}
          onClick={handleSend}
          loading={createMessage.isPending}
          disabled={!body.trim()}
        >
          Send
        </Button>
      </Stack>
    </Stack>
  );
}
